<template>
  <div class="details-wrapper">
    <section class="section">
      <h4 class="section-title">Ордер</h4>
      <div class="grid">
        <div class="row copy-row" v-for="r in orderRows" :key="r.label" :title="copyTitle(r.value)" @click="copyValue(r.value)">
          <span class="row-label">{{ r.label }}</span>
          <span class="row-value">{{ r.value }}</span>
        </div>
      </div>
    </section>

    <section class="section">
      <h4 class="section-title">Маршрут</h4>
      <div class="points" v-if="points.length">
        <div class="point-card" v-for="(pt, i) in points" :key="i">
          <div class="point-header">
            <span :class="['point-index', pt.loading ? 'point-loading' : 'point-unloading']">{{ i + 1 }}</span>
            <span class="point-city">
              {{ pt.title }}
              <span v-if="pt.operationLabel" class="point-op">({{ pt.operationLabel }})</span>
            </span>
          </div>
          <div class="grid pt-grid">
            <div class="row copy-row" v-for="r in pt.rows" :key="r.label" :title="copyTitle(r.value)" @click="copyValue(r.value)">
              <span class="row-label">{{ r.label }}</span>
              <span class="row-value">{{ r.value }}</span>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="empty">Нет данных по точкам маршрута.</div>
    </section>

    <section class="section">
      <h4 class="section-title">Цены</h4>
      <div class="grid">
        <div class="row copy-row" v-for="r in costRows" :key="r.label" :title="copyTitle(r.value)" @click="copyValue(r.value)">
          <span class="row-label">{{ r.label }}</span>
          <span class="row-value">{{ r.value }}</span>
        </div>
      </div>
    </section>

    <section class="section">
      <h4 class="section-title">Транспорт и Груз</h4>
      <div class="grid">
        <div class="row copy-row" v-for="r in transportCargoRows" :key="r.label" :title="copyTitle(r.value)" @click="copyValue(r.value)">
          <span class="row-label">{{ r.label }}</span>
          <span class="row-value">{{ r.value }}</span>
        </div>
      </div>
    </section>

    <section class="section">
      <h4 class="section-title">Задача</h4>
      <div class="grid">
        <div class="row copy-row" v-for="r in taskRows" :key="r.label" :title="copyTitle(r.value)" @click="copyValue(r.value)">
          <span class="row-label">{{ r.label }}</span>
          <span class="row-value">{{ r.value }}</span>
        </div>
      </div>
    </section>

    <section class="section" v-if="hasAdvise">
      <h4 class="section-title">Препрайсинг</h4>
      <AdviseRules :advise="order.context?.advise" />
    </section>

    <section class="section" v-if="comment && comment !== '\u2014'">
      <div class="section-header">
        <h4 class="section-title">Комментарий</h4>
        <button class="comment-toggle" @click="isCommentOpen = !isCommentOpen">
          {{ isCommentOpen ? 'Скрыть' : 'Показать' }}
        </button>
      </div>
      <div v-if="isCommentOpen" class="comment-text">{{ comment }}</div>
    </section>
  </div>
</template>

<script>
import {
  EMPTY_VALUE,
  formatMoney,
  getStatusMeta,
  getWinnerMeta,
} from '../helpers';
import { createToaster } from '@meforma/vue-toaster';
import AdviseRules from '@/components/advise/AdviseRules.vue';

const toaster = createToaster();

export default {
  name: 'PureBiddingDetails',
  components: { AdviseRules },
  props: {
    order: { type: Object, required: true },
  },
  data() {
    return { isCommentOpen: false };
  },
  computed: {
    orderRows() {
      const meta = this.order.meta || {};
      const statusMeta = getStatusMeta(this.order);
      return [
        { label: 'UID', value: this.v(this.order.uid) },
        { label: 'Trip ID', value: this.v(meta.trip_id) },
        { label: 'Платформа', value: this.v(meta.service_name) },
        { label: 'Клиент', value: this.v(meta.client_name) },
        { label: 'Статус', value: statusMeta.label },
        { label: 'Создан', value: this.dateTime(this.order.context?.create_dt) },
        { label: 'Обновлен', value: this.dateTime(this.order.context?.update_dt) },
        { label: 'Окончание', value: this.dateTime(this.order.context?.end_dt) },
      ];
    },
    points() {
      return (this.order.route?.points || []).map((pt) => ({
        title: this.v(pt.city_name),
        operationLabel: pt.operation_type === 'loading' ? 'Погрузка' : pt.operation_type === 'unloading' ? 'Разгрузка' : null,
        loading: pt.operation_type !== 'unloading',
        rows: [
          { label: '', value: this.v(pt.warehouse_name) },
          { label: 'Регион', value: this.v(pt.region_name) },
          { label: 'Индекс', value: this.v(pt.index) },
          { label: 'Плановое время', value: this.dateTime(pt.planed_date) },
        ],
      }));
    },
    costRows() {
      const cost = this.order.cost || {};
      const start = cost.start || {};
      const last = cost.last || {};
      return [
        { label: 'Валюта', value: this.v(cost.currency) },
        { label: 'Старт без НДС', value: this.money(start.without_nds) },
        { label: 'Старт с НДС', value: this.money(start.with_nds) },
        { label: 'Последняя без НДС', value: this.money(last.without_nds) },
        { label: 'Последняя с НДС', value: this.money(last.with_nds) },
        { label: 'Ранг', value: last.rank != null ? String(last.rank) : EMPTY_VALUE },
        { label: 'От бота', value: last.from_bot === true ? 'Да' : last.from_bot === false ? 'Нет' : EMPTY_VALUE },
        { label: 'Победитель', value: getWinnerMeta(this.order).text },
      ];
    },
    transportCargoRows() {
      const veh = this.order.veh || {};
      const cargo = this.order.cargo || {};
      return [
        { label: 'Тип ТС', value: this.v(veh.type) },
        { label: 'Тоннаж', value: this.num(veh.tonnage, 'т') },
        { label: 'Тип груза', value: this.v(cargo.list?.join(', ')) },
        { label: 'Вес', value: this.num(cargo.weight, 'кг') },
        { label: 'Объём', value: this.num(cargo.volume, 'м3') },
        { label: 'Паллеты', value: this.num(cargo.pallet) },
        { label: 'Опасный', value: cargo.hazardous ? 'Да' : 'Нет' },
      ];
    },
    taskRows() {
      const task = this.order.context?.task;
      if (!task) return [{ label: 'Задача', value: EMPTY_VALUE }];
      return [
        { label: 'Активна', value: task.active ? 'Да' : 'Нет' },
        { label: 'Значение', value: this.money(task.value) },
        { label: 'Ответственный', value: this.v(task.responsible) },
        { label: 'Обновлена', value: this.dateTime(task.updated_dt) },
      ];
    },
    hasAdvise() {
      const advise = this.order.context?.advise;
      return Array.isArray(advise) && advise.length > 0;
    },
    comment() {
      return this.v(this.order.info?.comment);
    },
  },
  methods: {
    v(val) {
      if (val === null || val === undefined || val === '') return EMPTY_VALUE;
      return String(val);
    },
    money(val) {
      if (typeof val !== 'number') return EMPTY_VALUE;
      return val.toLocaleString('ru-RU');
    },
    num(val, suffix) {
      if (typeof val !== 'number') return EMPTY_VALUE;
      const rounded = Number.isInteger(val) ? String(val) : val.toFixed(2);
      return `${rounded}${suffix ? ' ' + suffix : ''}`;
    },
    dateTime(val) {
      if (!val) return EMPTY_VALUE;
      return new Date(val).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    },
    copyTitle(value) {
      return value && value !== EMPTY_VALUE ? 'Скопировать' : '';
    },
    copyValue(value) {
      if (!value || value === EMPTY_VALUE) return;
      navigator.clipboard?.writeText(String(value)).then(() => {
        toaster.success('Скопировано');
      }).catch(() => {});
    },
  },
};
</script>

<style scoped>
.details-wrapper {
  padding: 10px 12px;
  font-size: 14px;
  color: #333;
  background: transparent;
}

.section {
  margin-bottom: 10px;
  padding: 0 0 10px;
  border-bottom: 1px solid #e5e7eb;
  background: transparent;
}

.section-title {
  font-size: 14px;
  font-weight: 700;
  margin: 0 0 10px;
  padding-bottom: 0;
  border-bottom: 0;
  color: #007bff;
  text-align: left;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.section-header .section-title { margin: 0; }

.comment-toggle {
  padding: 2px 8px;
  border: 1px solid #d7dce2;
  border-radius: 999px;
  background: #fff;
  color: #333;
  font-size: 13px;
  cursor: pointer;
}

.grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 8px;
}

.row {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-height: 42px;
  padding: 7px 8px;
}

.row-label {
  color: #7b8087;
  font-size: 12px;
  line-height: 1.1;
}

.row-value {
  font-size: 14px;
  line-height: 1.25;
  color: #333;
  font-weight: 500;
  word-break: break-word;
}

.copy-row {
  cursor: pointer;
  border: 1px solid transparent;
  border-radius: 6px;
  transition: background 0.15s, border-color 0.15s;
}

.copy-row:hover {
  background: #eef4ff;
  border-color: #bfdbfe;
}

.copy-row:hover .row-value {
  color: #1d4ed8;
}

.copy-value:hover {
  background: #eef4ff;
  border-color: #bfdbfe;
  color: #1d4ed8;
}

.empty { color: #999; font-size: 14px; }

.points { display: flex; flex-direction: column; gap: 10px; }

.point-card {
  padding: 10px;
  background: var(--details-card-bg, #e9edf3);
}

.point-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.point-index {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
}

.point-loading { background: #007bff; }
.point-unloading { background: #dc3545; }

.point-city { font-size: 15px; font-weight: 600; }
.point-op { font-weight: 400; color: #888; font-size: 13px; }
.pt-grid { padding-left: 30px; }

.comment-text { white-space: pre-wrap; color: #555; }

@media (max-width: 1600px) { .grid { grid-template-columns: repeat(5, minmax(0, 1fr)); } }
@media (max-width: 1200px) { .grid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
@media (max-width: 760px) { .grid { grid-template-columns: 1fr; } }
</style>
